import { useTranslation } from 'react-i18next'
import Select, { components } from 'react-select'
import CreatableSelect from 'react-select/creatable'

const normalizeText = (text) =>
  String(text ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

const filterOption = (option, rawInput) => {
  if (!rawInput) return true
  const haystack = normalizeText(option.label)
  return normalizeText(rawInput)
    .split(/\s+/)
    .every((word) => haystack.includes(word))
}

const cssVar = (name, fallback) => {
  if (typeof window === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

const CompactMultiValue = (props) => {
  const { index, getValue, selectProps } = props
  const maxVisible = Math.max(1, Number(selectProps.maxVisibleMultiValues) || 1)
  const values = getValue() || []
  const hidden = Math.max(0, values.length - maxVisible)

  if (index < maxVisible) {
    return <components.MultiValue {...props} />
  }

  if (index === maxVisible) {
    return (
      <div
        className="nomad-select__multi-value-overflow"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          flex: 'none',
          margin: '2px',
          padding: '1px 8px',
          borderRadius: 999,
          background: 'color-mix(in oklab, var(--primary, #00ab9b) 12%, transparent)',
          color: 'var(--foreground, #151b24)',
          fontSize: 12,
          fontWeight: 600,
          lineHeight: '20px',
          whiteSpace: 'nowrap',
        }}
        aria-hidden="true"
      >
        +{hidden}
      </div>
    )
  }

  return null
}

const buildStyles = (controlSize = 'default', { compactMulti = false } = {}) => {
  const isMoteur = controlSize === 'moteur'
  const border = cssVar(isMoteur ? '--border-strong' : '--input', '#e5e7eb')
  const ring = cssVar(isMoteur ? '--color-primary-600' : '--ring', '#008a7c')
  const card = cssVar('--card', '#ffffff')
  const foreground = cssVar('--foreground', '#151b24')
  const mutedFg = cssVar('--muted-foreground', '#6b7280')
  const accent = cssVar('--accent', '#eef2f9')
  const primarySoft = `color-mix(in oklab, ${cssVar('--primary', '#00ab9b')} 12%, transparent)`
  const radius = isMoteur
    ? 'var(--radius-sm, 6px)'
    : 'var(--radius-md, 8px)'
  const minHeight = isMoteur ? '40px' : 'var(--control-height, 36px)'
  const fontSize = isMoteur ? 12 : 14
  const valueFontSize = isMoteur ? 14 : 14
  const fixedHeight = compactMulti ? (isMoteur ? 40 : 36) : null

  return {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base, state) => ({
      ...base,
      minHeight: fixedHeight || minHeight,
      height: fixedHeight || (isMoteur ? 40 : base.height),
      maxHeight: fixedHeight || undefined,
      flexWrap: compactMulti ? 'nowrap' : base.flexWrap,
      overflow: compactMulti ? 'hidden' : base.overflow,
      borderRadius: radius,
      borderColor: state.isFocused ? ring : border,
      backgroundColor: card,
      boxShadow: state.isFocused
        ? isMoteur
          ? `0 0 0 2px color-mix(in oklab, ${cssVar('--color-primary-200', '#99ddd6')} 100%, transparent)`
          : `0 0 0 2px var(--background, #fff), 0 0 0 4px ${ring}`
        : isMoteur
          ? 'none'
          : 'var(--shadow-xs, none)',
      '&:hover': { borderColor: state.isFocused ? ring : border },
      fontSize,
    }),
    valueContainer: (base) => ({
      ...base,
      padding: isMoteur || compactMulti ? '0 8px' : base.padding,
      flexWrap: compactMulti ? 'nowrap' : base.flexWrap,
      overflow: compactMulti ? 'hidden' : base.overflow,
      flex: '1 1 auto',
      minWidth: 0,
    }),
    multiValue: (base) => ({
      ...base,
      flex: 'none',
      maxWidth: compactMulti ? '70%' : base.maxWidth,
      backgroundColor: accent,
      borderRadius: 999,
      margin: '2px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: foreground,
      fontSize: 12,
      fontWeight: 600,
      padding: '1px 6px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    multiValueRemove: (base) => ({
      ...base,
      borderRadius: 999,
      paddingLeft: 2,
      paddingRight: 4,
      ':hover': {
        backgroundColor: primarySoft,
        color: foreground,
      },
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: fixedHeight ? fixedHeight - 2 : isMoteur ? 38 : base.height,
      flex: 'none',
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: compactMulti ? 4 : base.padding,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: compactMulti ? 4 : base.padding,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 50,
      borderRadius: 'var(--radius-md, 8px)',
      overflow: 'hidden',
      border: `1px solid ${cssVar('--border', border)}`,
      boxShadow: 'var(--shadow-md)',
      backgroundColor: card,
    }),
    menuList: (base) => ({ ...base, padding: 4 }),
    option: (base, state) => ({
      ...base,
      borderRadius: 'var(--radius-sm, 6px)',
      fontSize: valueFontSize,
      backgroundColor: state.isSelected
        ? primarySoft
        : state.isFocused
          ? accent
          : 'transparent',
      color: foreground,
    }),
    placeholder: (base) => ({
      ...base,
      color: mutedFg,
      fontSize: valueFontSize,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    singleValue: (base) => ({ ...base, color: foreground, fontSize: valueFontSize }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      border: 'none',
      boxShadow: 'none',
      outline: 'none',
      fontSize: valueFontSize,
      color: foreground,
      ...(compactMulti
        ? {
            maxWidth: 48,
            gridTemplateColumns: '0 minmax(0, 1fr)',
          }
        : {}),
    }),
  }
}

const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder,
  isClearable = false,
  isDisabled = false,
  isLoading = false,
  isMulti = false,
  compactMulti = false,
  maxVisibleMultiValues = 1,
  formatOptionLabel,
  id,
  controlSize = 'default',
  creatable = false,
  onCreateOption,
  formatCreateLabel,
  'aria-label': ariaLabel,
}) => {
  const { t } = useTranslation()
  const selected = isMulti
    ? options.filter((o) =>
        (Array.isArray(value) ? value : []).some((v) => String(v) === String(o.value)),
      )
    : options.find((o) => String(o.value) === String(value)) ?? null
  const SelectComponent =
    creatable && typeof onCreateOption === 'function' ? CreatableSelect : Select
  const createProps =
    creatable && typeof onCreateOption === 'function'
      ? {
          onCreateOption,
          formatCreateLabel:
            formatCreateLabel ||
            ((inputValue) => {
              const text = String(inputValue || '').trim()
              return text ? `+ ${text}` : '+'
            }),
        }
      : {}

  const compact = Boolean(isMulti && compactMulti)

  return (
    <SelectComponent
      inputId={id}
      aria-label={ariaLabel}
      classNamePrefix="nomad-select"
      options={options}
      value={selected}
      onChange={(opt) => {
        if (isMulti) {
          onChange?.(Array.isArray(opt) ? opt.map((item) => item.value) : [])
          return
        }
        onChange?.(opt?.value ?? null)
      }}
      placeholder={placeholder ?? t('cartesAcces.shared.select.placeholder')}
      isSearchable
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      hideSelectedOptions={false}
      filterOption={filterOption}
      formatOptionLabel={formatOptionLabel}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      maxVisibleMultiValues={maxVisibleMultiValues}
      components={
        compact
          ? {
              MultiValue: CompactMultiValue,
            }
          : undefined
      }
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      styles={buildStyles(controlSize, { compactMulti: compact })}
      noOptionsMessage={() => t('cartesAcces.shared.select.noOptions')}
      {...createProps}
    />
  )
}

export default SearchableSelect
